<template>
    <AdminLayout>
        <template #topbar-search>
            <div class="flex flex-column ">
                <IconField>
                    <InputIcon class="pi pi-search" />
                    <InputText v-model="searchValue" placeholder="Search by keywords..." size="small" />
                </IconField>
            </div>
        </template>
        <template #header>
            <Breadcrumb :items="items" />
            <Button label="Export" class="accent-button" @click="ExportReviews" />
        </template>
        <div v-if="currentView !== 'Students Reviews'" class="back-section">
            <Button icon="pi pi-arrow-left" label="Back" class="p-button-text Caption back-button mb-2"
                @click="goBack" />
        </div>
        <div class="container">
            <div v-if="currentView === 'Students Reviews'" class="flex justify-content-end mb-4 md:px-4">
                <div class="flex align-items-center gap-2">
                    <label class="Label text-center">Sort By:</label>
                    <Select v-model="selectedSort" :options="sortOptions" optionLabel="label" optionValue="value"
                        placeholder="" class="w-36" />
                </div>
            </div>
            <StudentReviews v-if="currentView === 'Students Reviews'" :feedbacks="feedbacks"
                @view-feedback="viewFeedback" />
            <EditReview v-if="currentView === 'View review'" :feedback="selectedFeedback"
                @update-review="updateReviewHandler" @go-back="goBack" />

        </div>
    </AdminLayout>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import AdminLayout from "@/layout/AdminLayout.vue";
import Breadcrumb from "@/components/CustomBreadcrumb.vue";
import StudentReviews from "@/components/dashboard/admin/studentReviews/studentReviews.vue";
import EditReview from "@/components/dashboard/admin/studentReviews/editReview.vue";
import Button from 'primevue/button';
import Select from 'primevue/select';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';
import { useToast } from "primevue/usetoast";
import { notifySuccess } from '@/utils/notification';
import { getReviews, updateReview,exportReviews } from "@/api/admin";

const toast = useToast();
const currentView = ref('Students Reviews');
const selectedFeedback = ref(null);
const feedbacks = ref(null);
const selectedSort = ref("newest");
const searchValue = ref("");
const sortOptions = [
    { label: "Newest", value: "newest" },
    { label: "Oldest", value: "oldest" },
    { label: "Highest Rated", value: "highest" },
    { label: "Lowest Rated", value: "lowest" },
];

const fetchStudentReviews = async () => {
    try {
        const response = await getReviews({ sortBy: selectedSort.value, searchTerm: searchValue.value });
        feedbacks.value = response.data.data.reviewsList
    } catch (error) {
        console.error("Failed to fetch reviews:", error);
    }
};

onMounted(fetchStudentReviews);

watch([selectedSort, searchValue], () => {
  fetchStudentReviews();
});

const items = computed(() => {
    const breadcrumbItems = [{ label: "Students Reviews" }];

    if (currentView.value === "View review") {
        breadcrumbItems.push({ label: "View review" });
    } else if (currentView.value === "add review") {
        breadcrumbItems.push({ label: "add review" });
    }
    return breadcrumbItems;
});

const updateReviewHandler = async (updatedReview) => {
    try {
        await updateReview(updatedReview.id, {
            review: updatedReview.review,
            published: updatedReview.published,
        });

        toast.add({
            severity: "success",
            summary: "Review Updated",
            detail: "Review updated successfully.",
            life: 3000,
        });

        await fetchStudentReviews();
        goBack();
    } catch (error) {
        toast.add({
            severity: "error",
            summary: "Update Failed",
            detail: error.response?.data?.message || "Unable to update review.",
            life: 3000,
        });
    }
};
const ExportReviews = async () => {
  try {
    const res = await exportReviews({searchTerm: searchValue.value,sortBy: selectedSort.value});
    const filePath = res.data.data.downloadLink;
    const link = document.createElement("a");
    link.href = filePath;
    link.download = "reviews.csv";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    notifySuccess("Success!", "The file has been downloaded.");
  } catch (err) {
    console.error("Failed to export", err);
  }
}
const showList = () => {
    currentView.value = 'Students Reviews';
};
const viewFeedback = (feedback) => {
    selectedFeedback.value = feedback;
    currentView.value = "View review";
};
const goBack = () => {
    showList();
};

</script>

<style>
.container {
    width: 100%;
    height: auto;
    border-radius: 8px;
}

.back-section {
    display: flex;
    align-items: center;
}

.back-button {
    color: var(--gray-600) !important;
    font-size: 12px !important;
}

.back-button:hover {
    color: var(--gray-600) !important;
    background-color: var(--gray-200) !important;
}

.back-button:active {
    color: var(--gray-600) !important;
}

.pi-arrow-left {
    color: var(--gray-600) !important;
    font-size: 12px !important;
}
</style>
